import { DecimalPipe, DatePipe } from '@angular/common';
import {
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  inject,
} from '@angular/core';
import { MatButton } from '@angular/material/button';
import { MatSort, MatSortHeader } from '@angular/material/sort';
import {
  MatTableDataSource,
  MatTable,
  MatColumnDef,
  MatHeaderCellDef,
  MatHeaderCell,
  MatCellDef,
  MatCell,
  MatHeaderRowDef,
  MatHeaderRow,
  MatRowDef,
  MatRow,
} from '@angular/material/table';
import { DialogService, DIALOG_RETURN_TYPES } from '@next/core-lib/dialog';
import { LocaleService, TranslatePipe } from '@next/core-lib/i18n';
import { SiteState } from '@next/core-lib/site';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

import { DrillCowListComponent } from 'src/app/shared/components/drill-cow-list/drill-cow-list.component';

import * as HealthActions from '../../store/actions/health.actions';
import { getHealthYearSummary } from '../../store/selectors/health.selectors';

@Component({
  selector: 'app-year-summary',
  templateUrl: './health-year-summary.component.html',
  styleUrls: ['./health-year-summary.component.scss'],
  imports: [
    MatTable,
    MatSort,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatSortHeader,
    MatCellDef,
    MatCell,
    MatButton,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    DecimalPipe,
    DatePipe,
    TranslatePipe,
  ],
})
export class HealthYearSummaryComponent implements OnInit, OnDestroy, OnChanges {
  private readonly store = inject<Store<SiteState>>(Store);
  private readonly dialogService = inject(DialogService);
  private readonly localeService = inject(LocaleService);

  @Input()
  public selectedDate: string;
  public columns: { id: string; label: string; type: 'string' | 'drill' }[] = [
    { id: 'name', label: 'health.health-year-summary.column.name', type: 'string' },
    {
      id: 'currentMeasurement',
      label: 'health.health-year-summary.column.currentMeasurement',
      type: 'drill',
    },
    {
      id: 'previous3Months',
      label: 'health.health-year-summary.column.previous3Months',
      type: 'drill',
    },
    {
      id: 'previous6Months',
      label: 'health.health-year-summary.column.previous6Months',
      type: 'drill',
    },
    {
      id: 'previous12Months',
      label: 'health.health-year-summary.column.previous12Months',
      type: 'drill',
    },
  ];
  public dataSource = new MatTableDataSource<TableData>();
  private getYearHealtSummary$ = this.store.select(getHealthYearSummary);
  private readonly destroy$ = new Subject<void>();

  ngOnInit(): void {
    this.getYearHealtSummary$
      .pipe(
        filter((healthYearSummary) => !!healthYearSummary),
        takeUntil(this.destroy$),
      )
      .subscribe((healthYearSummary) => {
        this.dataSource.data = healthYearSummary.groups.map<TableData>((group) => ({
          name: group.name,
          currentMeasurement: {
            cows: group.currentMeasurement.cows,
            cowsWithAttention: group.currentMeasurement.cowsWithAttention,
            percentage: group.currentMeasurement.percentage,
            value: group.currentMeasurement.numberOfAnimalsWithAttention,
          },
          previous3Months: {
            cows: group.previous3Months.cows,
            cowsWithAttention: group.previous3Months.cowsWithAttention,
            percentage: group.previous3Months.percentage,
            value: group.previous3Months.numberOfAnimalsWithAttention,
          },
          previous6Months: {
            cows: group.previous6Months.cows,
            cowsWithAttention: group.previous6Months.cowsWithAttention,
            percentage: group.previous6Months.percentage,
            value: group.previous6Months.numberOfAnimalsWithAttention,
          },
          previous12Months: {
            cows: group.previous12Months.cows,
            cowsWithAttention: group.previous12Months.cowsWithAttention,
            percentage: group.previous12Months.percentage,
            value: group.previous12Months.numberOfAnimalsWithAttention,
          },
        }));
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectedDate'].currentValue !== changes['selectedDate'].previousValue) {
      const selectedDate = changes['selectedDate'].currentValue as string;
      this.store.dispatch(
        HealthActions.loadHealthYearSummaryRequest({
          date: new Date(selectedDate).toISOString(),
        }),
      );
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public columnIds = (): string[] => this.columns.map((item) => item.id);

  public openDrillCowList(cows: string[], cowsWithAttention: string[]): void {
    const cowList = [...cows]
      .sort()
      .map((cow) => ({ internationalCowId: cow, emphasize: cowsWithAttention.indexOf(cow) > -1 }));

    this.dialogService
      .fullscreenDialog(DrillCowListComponent, {
        cows: cowList,
        title: this.localeService.translate('dairyresults.cow-details.title'),
        setPendingChanges: false,
        fullscreen: true,
      })
      .subscribe(({ status }) => {
        if (status === DIALOG_RETURN_TYPES.confirm) {
          // Handle the confirmation status but do nothing
        }
      });
  }
}

interface TableData {
  name: string;
  currentMeasurement: TableCellData;
  previous3Months: TableCellData;
  previous6Months: TableCellData;
  previous12Months: TableCellData;
}

interface TableCellData {
  value: number;
  percentage: number;
  cows: string[];
  cowsWithAttention: string[];
}
